import React, { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useIsDarkMode, useIsGlass } from "../../theme/ThemeContext";
import { leadBranch, wasContacted } from "./utilities";

// Analytics board for the roles that watch the whole company (Admin, Web Lead
// Manager, Executive Team). Everything is derived from the leads already in
// memory — no extra endpoints — so it follows the page's branch scope.

// Four-slot categorical palette, fixed order, never cycled. Validated for
// CVD separation and >=3:1 contrast against BOTH the light (#ffffff) and dark
// (#1e293b) .app-card surfaces, so one set serves both themes.
const SERIES = {
  open: "#0284c7", // sky
  won: "#059669", // emerald
  noSale: "#8b5cf6", // violet
  removed: "#d97706", // amber
};

// The sky↔violet pair sits in the CVD floor band, so every chart that uses
// more than one series also ships a legend, a tooltip and 2px surface gaps.
//
// Recharts writes axis/grid/label colors as literal SVG attributes, so they
// can't come from a `dark:` class — they're picked from the theme instead. One
// mid-slate can't stay legible on both a white and a slate-800 card, so each
// role carries a light/dark pair.
const AXIS_TICK_FILL = { light: "#475569", dark: "#94a3b8" }; // slate-600 / 400
const GRID_STROKE_COLOR = { light: "#e2e8f0", dark: "#334155" }; // slate-200 / 700

const chartTheme = (isDark, isGlass) => {
  const key = isDark ? "dark" : "light";
  return {
    axisTick: { fill: AXIS_TICK_FILL[key], fontSize: 11 },
    gridStroke: GRID_STROKE_COLOR[key],
    // Bar-end value labels sit on the card, not on the bar.
    labelFill: AXIS_TICK_FILL[key],
    // Recharts paints the hover band with this; keep it a wash, not a block.
    cursorFill: isDark ? "#33415566" : "#64748b26",
    // Stacked-bar separators read as gaps only if they match the card behind.
    // Liquid Glass dark cards are translucent over a near-black wallpaper, so
    // they read darker than the opaque slate-800 card.
    surface: isDark ? (isGlass ? "#151d2e" : "#1e293b") : "#ffffff",
  };
};

const RANGES = [
  { months: 3, label: "3 mo" },
  { months: 6, label: "6 mo" },
  { months: 12, label: "12 mo" },
];

const COLLAPSE_KEY = "webleads.analytics.collapsed";

// Form values are written for the customer, not for an axis — the long ones
// blow out the category gutter. Full text stays in the tooltip.
const SOURCE_LABELS = {
  "Social (Facebook, Instagram, LinkedIn)": "Social",
  "Radio Advertisement": "Radio",
  "TV Advertisement": "TV",
  "Lumber / Building Materials": "Lumber / Building",
};

const startOfRange = (months) => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(1);
  d.setMonth(d.getMonth() - (months - 1));
  return d;
};

const monthKey = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const monthLabel = (date) =>
  new Date(date).toLocaleDateString("en-US", {
    month: "short",
    year: "2-digit",
  });

const firstContactDate = (lead) => {
  const contact = lead.leadProgress?.contactStatus || {};
  const stamps = [
    contact.phoneContact?.status ? contact.phoneContact?.date : null,
    contact.emailContact?.status ? contact.emailContact?.date : null,
  ]
    .filter(Boolean)
    .map((d) => new Date(d).getTime())
    .filter((t) => !Number.isNaN(t));
  return stamps.length ? new Date(Math.min(...stamps)) : null;
};

const median = (values) => {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[mid]
    : Math.round(((sorted[mid - 1] + sorted[mid]) / 2) * 10) / 10;
};

const pct = (part, whole) => (whole ? Math.round((part / whole) * 100) : 0);

// ------------------------------------------------------------------ pieces

const Panel = ({ title, subtitle, aside, className = "", children }) => (
  <div className={`app-card min-w-0 rounded-2xl border p-5 ${className}`}>
    <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
      <div className="min-w-0">
        <h3 className="font-semibold tracking-tight">{title}</h3>
        {subtitle && <p className="app-muted mt-0.5 text-xs">{subtitle}</p>}
      </div>
      {aside}
    </div>
    {children}
  </div>
);

const ChartTooltip = ({ active, payload, label, showTotal = true }) => {
  if (!active || !payload?.length) return null;
  const rows = payload.filter((row) => row.value > 0);
  if (!rows.length) return null;
  const total = payload.reduce((sum, row) => sum + (row.value || 0), 0);
  return (
    // Floating layer over the plot: glass material, not a (translucent) card.
    <div className="app-glass rounded-xl border px-3 py-2 text-xs">
      <p className="font-semibold">{label}</p>
      {rows.map((row) => (
        <p key={row.name} className="mt-1 flex items-center gap-1.5">
          <span
            className="h-2 w-2 shrink-0 rounded-sm"
            style={{ backgroundColor: row.color || row.fill }}
          />
          <span className="app-muted">{row.name}</span>
          <span className="ml-auto pl-3 font-bold tabular-nums">{row.value}</span>
        </p>
      ))}
      {showTotal && rows.length > 1 && (
        <p className="app-divider mt-1.5 flex items-center gap-1.5 border-t pt-1.5">
          <span className="app-muted">Total</span>
          <span className="ml-auto pl-3 font-bold tabular-nums">{total}</span>
        </p>
      )}
    </div>
  );
};

const legendLabel = (value) => (
  <span className="app-muted text-xs font-semibold">{value}</span>
);

const EmptyPlot = ({ height, message }) => (
  <div
    className="app-muted flex items-center justify-center text-xs"
    style={{ height }}
  >
    {message}
  </div>
);

// Funnel stages are one measure at four points, so they stay one hue and get
// direct labels rather than a legend.
const FunnelRow = ({ label, value, share, conversion }) => (
  <div>
    <div className="flex items-baseline justify-between gap-2 text-xs">
      <span className="font-semibold">{label}</span>
      <span className="app-muted">
        {/* Text, not a mark: the sky-600 series fill is legible on white but
            muddies on the slate-800 card, so it steps up a few shades. */}
        <span className="font-bold tabular-nums text-sky-700 dark:text-sky-300">{value}</span>
        {conversion !== null && ` · ${conversion}% of previous`}
      </span>
    </div>
    <div className="mt-1.5 h-2.5 w-full overflow-hidden rounded-full bg-[color:var(--app-hover)]">
      <div
        className="h-full rounded-full"
        style={{ width: `${share}%`, backgroundColor: SERIES.open }}
      />
    </div>
  </div>
);

// ------------------------------------------------------------------- board

const LeadAnalytics = ({
  leads,
  historyLoading,
  scopeLabel,
  showByLocation,
}) => {
  const isDark = useIsDarkMode();
  const isGlass = useIsGlass();
  const theme = chartTheme(isDark, isGlass);
  const { axisTick: AXIS_TICK, gridStroke: GRID_STROKE, surface } = theme;
  const [months, setMonths] = useState(6);
  // Collapsed unless the user has opened it before — five charts shouldn't
  // push the lead list below the fold for people who never look at them.
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSE_KEY) !== "0"
  );

  const toggleCollapsed = () =>
    setCollapsed((prev) => {
      localStorage.setItem(COLLAPSE_KEY, prev ? "0" : "1");
      return !prev;
    });

  // Every lead entered inside the window, tagged with the outcome it reached.
  // Active/Completed/Deleted are disjoint server-side, so nothing double counts.
  const inWindow = useMemo(() => {
    const start = startOfRange(months).getTime();
    const tag = (list, outcome) =>
      (list || [])
        .filter((lead) => {
          const entered = new Date(lead.webleadEntry?.entryDate).getTime();
          return !Number.isNaN(entered) && entered >= start;
        })
        .map((lead) => ({
          lead,
          outcome:
            outcome === "completed"
              ? lead.saleData?.orderNumber
                ? "won"
                : "noSale"
              : outcome,
        }));
    return [
      ...tag(leads.Active, "open"),
      ...tag(leads.Completed, "completed"),
      ...tag(leads.Deleted, "removed"),
    ];
  }, [leads, months]);

  // Deleted leads are duplicates and spam; they skew every rate, so the
  // funnel, sources and response times all run on the live book only.
  const workable = useMemo(
    () => inWindow.filter((row) => row.outcome !== "removed"),
    [inWindow]
  );

  const funnel = useMemo(() => {
    const received = workable.length;
    const contacted = workable.filter((row) => wasContacted(row.lead)).length;
    const quoted = workable.filter(
      (row) => row.lead.saleData?.quoteNumber || row.lead.saleData?.orderNumber
    ).length;
    const won = workable.filter((row) => row.lead.saleData?.orderNumber).length;
    return [
      { label: "Leads received", value: received, conversion: null },
      { label: "Contacted", value: contacted, conversion: pct(contacted, received) },
      { label: "Quoted", value: quoted, conversion: pct(quoted, contacted) },
      { label: "Won (order placed)", value: won, conversion: pct(won, quoted) },
    ].map((stage) => ({ ...stage, share: pct(stage.value, received) }));
  }, [workable]);

  const byMonth = useMemo(() => {
    const buckets = new Map();
    const cursor = startOfRange(months);
    for (let i = 0; i < months; i++) {
      buckets.set(monthKey(cursor), {
        month: monthLabel(cursor),
        open: 0,
        won: 0,
        noSale: 0,
        removed: 0,
      });
      cursor.setMonth(cursor.getMonth() + 1);
    }
    inWindow.forEach(({ lead, outcome }) => {
      const bucket = buckets.get(monthKey(lead.webleadEntry?.entryDate));
      if (bucket) bucket[outcome] += 1;
    });
    return [...buckets.values()];
  }, [inWindow, months]);

  const byLocation = useMemo(() => {
    if (!showByLocation) return [];
    const rows = new Map();
    inWindow.forEach(({ lead, outcome }) => {
      if (outcome === "removed") return;
      const branch = leadBranch(lead) || "Unassigned";
      const row = rows.get(branch) || { branch, open: 0, won: 0, noSale: 0 };
      row[outcome] += 1;
      rows.set(branch, row);
    });
    return [...rows.values()]
      .map((row) => ({ ...row, total: row.open + row.won + row.noSale }))
      .sort((a, b) => b.total - a.total || a.branch.localeCompare(b.branch));
  }, [inWindow, showByLocation]);

  // Single measure by identity -> one hue, sorted, top 7 with the tail folded
  // into "Other" rather than given a generated color.
  const bySource = useMemo(() => {
    const counts = new Map();
    workable.forEach(({ lead }) => {
      const source = lead.webleadEntry?.hearAbout || "Not given";
      counts.set(source, (counts.get(source) || 0) + 1);
    });
    const sorted = [...counts.entries()]
      .map(([source, count]) => ({
        source: SOURCE_LABELS[source] || source,
        count,
      }))
      .sort((a, b) => b.count - a.count);
    if (sorted.length <= 8) return sorted;
    const tail = sorted.slice(7).reduce((sum, row) => sum + row.count, 0);
    return [...sorted.slice(0, 7), { source: "Other", count: tail }];
  }, [workable]);

  const responseTime = useMemo(() => {
    const buckets = [
      { bucket: "Same day", count: 0 },
      { bucket: "1 day", count: 0 },
      { bucket: "2–3 days", count: 0 },
      { bucket: "4–7 days", count: 0 },
      { bucket: "8+ days", count: 0 },
      { bucket: "No contact", count: 0 },
    ];
    const days = [];
    workable.forEach(({ lead }) => {
      const contacted = firstContactDate(lead);
      if (!contacted) {
        buckets[5].count += 1;
        return;
      }
      const entered = new Date(lead.webleadEntry?.entryDate).getTime();
      const elapsed = Math.max(
        0,
        Math.floor((contacted.getTime() - entered) / 86400000)
      );
      days.push(elapsed);
      const index =
        elapsed === 0 ? 0 : elapsed === 1 ? 1 : elapsed <= 3 ? 2 : elapsed <= 7 ? 3 : 4;
      buckets[index].count += 1;
    });
    return { buckets, median: median(days) };
  }, [workable]);

  const pending = historyLoading || !leads.Completed;
  const rangeLabel =
    months === 12 ? "last 12 months" : `last ${months} months`;

  return (
    <section>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={toggleCollapsed}
          aria-expanded={!collapsed}
          className="-ml-1 inline-flex min-h-[36px] items-center gap-1.5 rounded-full px-1 pr-2 text-base font-semibold tracking-tight transition-colors hover:text-[color:var(--app-accent)]"
        >
          <ExpandMoreIcon
            fontSize="small"
            className={`app-muted transition-transform ${collapsed ? "-rotate-90" : ""}`}
          />
          Analytics
        </button>
        {!collapsed && (
          <div
            role="group"
            aria-label="Date range"
            className="ml-auto flex items-center gap-0.5 rounded-xl bg-[color:var(--app-hover)] p-[3px]"
          >
            {RANGES.map((range) => (
              <button
                type="button"
                key={range.months}
                onClick={() => setMonths(range.months)}
                aria-pressed={months === range.months}
                className={`min-h-[32px] rounded-lg px-3 text-xs font-semibold transition-colors ${
                  months === range.months
                    ? "bg-white text-[color:var(--app-text)] shadow-sm dark:bg-white/[0.14] dark:shadow-none"
                    : "app-muted hover:text-[color:var(--app-text)]"
                }`}
              >
                {range.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {!collapsed && (
        <>
          {pending && (
            <p className="app-muted mb-3 text-xs">
              Completed and deleted history is still loading — outcome numbers
              will fill in shortly.
            </p>
          )}

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Panel
              title="Conversion funnel"
              subtitle={`${scopeLabel} · ${rangeLabel}`}
            >
              {funnel[0].value === 0 ? (
                <EmptyPlot height={180} message="No leads in this window." />
              ) : (
                <div className="flex flex-col gap-3 py-1">
                  {funnel.map((stage) => (
                    <FunnelRow key={stage.label} {...stage} />
                  ))}
                  <p className="app-muted app-divider border-t pt-2 text-xs">
                    <span className="text-base font-bold tabular-nums text-emerald-700 dark:text-emerald-300">
                      {funnel[3].share}%
                    </span>{" "}
                    of every lead received ended in an order.
                  </p>
                </div>
              )}
            </Panel>

            <Panel
              className="lg:col-span-2"
              title="Leads by month"
              subtitle="Grouped by the month the lead came in, colored by where it ended up"
            >
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={byMonth} barCategoryGap="22%">
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={GRID_STROKE}
                    vertical={false}
                  />
                  <XAxis dataKey="month" tick={AXIS_TICK} tickLine={false} />
                  <YAxis
                    tick={AXIS_TICK}
                    tickLine={false}
                    axisLine={false}
                    width={32}
                    allowDecimals={false}
                  />
                  <Tooltip
                    cursor={{ fill: theme.cursorFill }}
                    content={<ChartTooltip />}
                  />
                  <Legend formatter={legendLabel} iconSize={9} />
                  <Bar
                    dataKey="won"
                    stackId="a"
                    name="Won"
                    fill={SERIES.won}
                    stroke={surface}
                    strokeWidth={2}
                  />
                  <Bar
                    dataKey="noSale"
                    stackId="a"
                    name="Closed, no sale"
                    fill={SERIES.noSale}
                    stroke={surface}
                    strokeWidth={2}
                  />
                  <Bar
                    dataKey="open"
                    stackId="a"
                    name="Still open"
                    fill={SERIES.open}
                    stroke={surface}
                    strokeWidth={2}
                  />
                  <Bar
                    dataKey="removed"
                    stackId="a"
                    name="Deleted"
                    fill={SERIES.removed}
                    stroke={surface}
                    strokeWidth={2}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Panel>

            {showByLocation && (
              <Panel
                title="Outcomes by location"
                subtitle={`Leads received in the ${rangeLabel}, busiest branch first`}
              >
                {byLocation.length === 0 ? (
                  <EmptyPlot height={220} message="No leads in this window." />
                ) : (
                  <ResponsiveContainer
                    width="100%"
                    height={Math.max(220, byLocation.length * 26 + 60)}
                  >
                    <BarChart
                      data={byLocation}
                      layout="vertical"
                      barCategoryGap="24%"
                      margin={{ left: 8, right: 16 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={GRID_STROKE}
                        horizontal={false}
                      />
                      <XAxis
                        type="number"
                        tick={AXIS_TICK}
                        tickLine={false}
                        allowDecimals={false}
                      />
                      <YAxis
                        type="category"
                        dataKey="branch"
                        tick={AXIS_TICK}
                        tickLine={false}
                        axisLine={false}
                        width={120}
                      />
                      <Tooltip
                        cursor={{ fill: theme.cursorFill }}
                        content={<ChartTooltip />}
                      />
                      <Legend formatter={legendLabel} iconSize={9} />
                      <Bar
                        dataKey="won"
                        stackId="a"
                        name="Won"
                        fill={SERIES.won}
                        stroke={surface}
                        strokeWidth={2}
                      />
                      <Bar
                        dataKey="noSale"
                        stackId="a"
                        name="Closed, no sale"
                        fill={SERIES.noSale}
                        stroke={surface}
                        strokeWidth={2}
                      />
                      <Bar
                        dataKey="open"
                        stackId="a"
                        name="Still open"
                        fill={SERIES.open}
                        stroke={surface}
                        strokeWidth={2}
                        radius={[0, 4, 4, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Panel>
            )}

            <Panel
              title="How they found us"
              subtitle={`Source on the form · ${rangeLabel}`}
            >
              {bySource.length === 0 ? (
                <EmptyPlot height={240} message="No leads in this window." />
              ) : (
                <ResponsiveContainer
                  width="100%"
                  height={Math.max(200, bySource.length * 28 + 30)}
                >
                  <BarChart
                    data={bySource}
                    layout="vertical"
                    barCategoryGap="26%"
                    margin={{ left: 8, right: 28 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={GRID_STROKE}
                      horizontal={false}
                    />
                    <XAxis
                      type="number"
                      tick={AXIS_TICK}
                      tickLine={false}
                      allowDecimals={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="source"
                      tick={AXIS_TICK}
                      tickLine={false}
                      axisLine={false}
                      width={130}
                    />
                    <Tooltip
                      cursor={{ fill: theme.cursorFill }}
                      content={<ChartTooltip showTotal={false} />}
                    />
                    <Bar
                      dataKey="count"
                      name="Leads"
                      fill={SERIES.open}
                      radius={[0, 4, 4, 0]}
                      label={{
                        position: "right",
                        fill: theme.labelFill,
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Panel>

            <Panel
              title="Time to first contact"
              subtitle={`How long customers waited · ${rangeLabel}`}
              aside={
                <p className="app-muted text-xs">
                  Median{" "}
                  <span className="text-base font-bold tabular-nums text-sky-700 dark:text-sky-300">
                    {responseTime.median === null
                      ? "—"
                      : responseTime.median === 0
                      ? "same day"
                      : `${responseTime.median} d`}
                  </span>
                </p>
              }
            >
              {workable.length === 0 ? (
                <EmptyPlot height={240} message="No leads in this window." />
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart
                    data={responseTime.buckets}
                    barCategoryGap="26%"
                    margin={{ top: 16 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={GRID_STROKE}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="bucket"
                      tick={AXIS_TICK}
                      tickLine={false}
                      interval={0}
                      angle={-20}
                      textAnchor="end"
                      height={50}
                    />
                    <YAxis
                      tick={AXIS_TICK}
                      tickLine={false}
                      axisLine={false}
                      width={32}
                      allowDecimals={false}
                    />
                    <Tooltip
                      cursor={{ fill: theme.cursorFill }}
                      content={<ChartTooltip showTotal={false} />}
                    />
                    <Bar
                      dataKey="count"
                      name="Leads"
                      fill={SERIES.open}
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Panel>
          </div>
        </>
      )}
    </section>
  );
};

export default LeadAnalytics;
